import { initializeApp } from 'firebase/app'
import {
    Timestamp,
    addDoc,
    and,
    arrayUnion,
    collection,
    deleteField,
    doc,
    getDoc,
    getDocs,
    getFirestore,
    or,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore'
import {
    AdditionalFee,
    Dismantle,
    BookOrder,
    CalendarRule,
    CalendarRulesOverride,
    CleanseData,
    CommercialAccount,
    CommercialSite,
    Confirmation,
    ConfirmationQuestionAnswer,
    DisciplinaryAction,
    Holiday,
    InternalComment,
    Item,
    JobMonitor,
    JobStatus,
    KeyEventsTimeline,
    List,
    LiveOperation,
    MissionControl,
    PayoutCategory,
    PayoutReview,
    PolicyRule,
    PostcodesData,
    ServiceMessage,
    ServicePricing,
    Status,
    Surge,
} from '../types'
import axios, { AxiosError } from 'axios'
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage'
import { getAuth } from 'firebase/auth'
import { trackInternalEvent } from '@/lib/tracking/track'
import { formatDetailChangeMessage } from '@/lib/utils/formatting'
import { getStagingAuthDomain } from './auth'

const urlParams = new URLSearchParams(window.location.search)

console.log('APP TARGET CHECK', import.meta.env.VITE_APP_TARGET === 'staging')

// Preview channels (beehive-litta-co--<channel>.web.app) are always staging:
// their builds carry the staging Litta API credentials, and prod must not be
// reachable from a preview link (use a local dev server with a prod .env for
// that). The prod default domain beehive-litta-co.web.app has no "--".
export const isStaging =
    urlParams.get('staging') === 'true' ||
    import.meta.env.VITE_APP_TARGET === 'staging' ||
    /^beehive-litta-co--.+\.web\.app$/.test(window.location.hostname)
export const isQA = import.meta.env.VITE_APP_TARGET === 'qa'
console.log(`INITIALISING FIREBASE MODULE AS ${isStaging ? 'STAGING' : 'LIVE'}`)

// Cannot import getLittaPublicApiBaseUrl from litta-api-fetchers (circular dep).
// Mirrors the exact same logic inline, including the localhost override.
const LITTA_API_LOCAL_URL = 'http://localhost:3005'
const LITTA_API_STAGING_URL =
    (import.meta.env.VITE_LITTA_API_URL_STAGING as string) || ''
const LITTA_LOCAL_KEY = 'littaApi:useLocal'
const LITTA_OVERRIDE_KEY = 'littaApi:overrideUrl'
const TRUSTED_OVERRIDE_HOST = /^litta-api(-pr-\d+)?-owldxgx2oa-nw\.a\.run\.app$/
const isTrustedOverrideUrl = (url: string) => {
    try {
        const parsed = new URL(url)
        if (
            parsed.hostname === 'localhost' ||
            parsed.hostname === '127.0.0.1'
        ) {
            return true
        }
        return (
            parsed.protocol === 'https:' &&
            TRUSTED_OVERRIDE_HOST.test(parsed.hostname)
        )
    } catch {
        return false
    }
}
const getLittaPublicApiBaseUrl = () => {
    if (isStaging) {
        const override = localStorage
            .getItem(LITTA_OVERRIDE_KEY)
            ?.trim()
            .replace(/\/+$/, '')
        if (override && isTrustedOverrideUrl(override)) return override
        if (localStorage.getItem(LITTA_LOCAL_KEY) === 'true')
            return LITTA_API_LOCAL_URL
        if (LITTA_API_STAGING_URL) return LITTA_API_STAGING_URL
    }
    return import.meta.env.VITE_LITTA_API_URL as string
}

const liveConfig = {
    apiKey: 'AIzaSyB0ARr2yDk_Glu2C8nhZkjs95-xW3vgrK8',
    authDomain: 'beehive.litta.co',
    databaseURL: 'https://book-litta-co-default-rtdb.firebaseio.com',
    projectId: 'book-litta-co',
    storageBucket: 'book-litta-co.appspot.com',
    messagingSenderId: '1023222438587',
    appId: '1:1023222438587:web:7e28bfdd72a4c3f565e79e',
}

const stagingConfig = {
    apiKey: 'AIzaSyCRWgOq2jqKKgF_jfDowHgF98S7PzOJvBw',
    authDomain: getStagingAuthDomain(window.location.hostname),
    databaseURL:
        'https://staging-9c37f-default-rtdb.europe-west1.firebasedatabase.app',
    projectId: 'staging-9c37f',
    storageBucket: 'staging-9c37f.appspot.com',
    messagingSenderId: '115765691648',
    appId: '1:115765691648:web:cc4e4c4cc9a1b2af519fe5',
}

export const firebaseApp = initializeApp(isStaging ? stagingConfig : liveConfig)

export const db = getFirestore(firebaseApp)
const storage = getStorage(firebaseApp)

const holidaysRef = collection(db, 'holidays')
const calendarRulesOverrideRef = collection(db, 'calendar_rules_override')

export const uploadFile = async (file: File) => {
    const storageRef = ref(storage, `${new Date().getTime()}_${file.name}`)
    try {
        const result = await uploadBytes(storageRef, file)
        return await getDownloadURL(result.ref)
    } catch (err) {
        console.log(err)
        return null
    }
}

export const getJobStatuses = async (): Promise<JobStatus[] | null> => {
    const jobStatusesRef = collection(db, 'jobStatuses')
    const whereByActive = where('haystackFilterActive', '==', true)
    const queryJobStatuses = query(jobStatusesRef, whereByActive)

    const snapshot = await getDocs(queryJobStatuses)

    if (snapshot.empty) return null
    return snapshot.docs.map(doc => doc.data() as JobStatus)
}

export const ordersRef = collection(db, 'book_orders')
export const callListRef = collection(db, 'callList')
export const usersRef = collection(db, 'users')
export const zendeskRef = collection(db, 'zendesk')
const trustpilotRef = collection(db, 'trustpilot_outstanding_replies')

export const trustpilotOutstanding = doc(trustpilotRef, 'outstanding_replies')

export const getServiceMessages = async (
    sessionStorage: Storage,
): Promise<ServiceMessage[]> => {
    const cachedMessages = sessionStorage.getItem('serviceMessages')

    if (cachedMessages) {
        console.log('Retrieved service messages from cache')
        return JSON.parse(cachedMessages) as ServiceMessage[]
    }

    const messagesRef = collection(db, 'service_messages')
    const snapshot = await getDocs(messagesRef)

    if (snapshot.empty) return []
    const serviceMessages = snapshot.docs.map(
        doc => doc.data() as ServiceMessage,
    )

    console.log('Adding service messages to cache')
    sessionStorage.setItem('serviceMessages', JSON.stringify(serviceMessages))

    return serviceMessages
}

const getAccessToken = async () => {
    const refreshToken =
        '1//03KddvpJaEUrdCgYIARAAGAMSNwF-L9IruIdaya7JkIH83_POJcc2MAjO3SfaoCy5jPOQfiOTX5Y_9Vl8yQa6kqnIapE_y44n5io'

    const data = JSON.stringify({
        refresh_token: refreshToken,
        client_id:
            '378934482809-2pb4k84alqpudk4pmnhj0qaiec5i93lq.apps.googleusercontent.com',
        client_secret: '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',
        grant_type: 'refresh_token',
    })

    const response = await axios.post(
        'https://oauth2.googleapis.com/token',
        data,
        {
            headers: { 'Content-Type': 'application/json' },
        },
    )

    return response.data.access_token
}

const getNameFromEmail = (email: string) => {
    return (
        email.charAt(0).toUpperCase() + email.substring(1, email.indexOf('@'))
    )
}

const emailEncoder = (
    fromName: string,
    fromEmail: string,
    toEmail: string,
    subject: string,
    ...messageContents: string[]
) => {
    // Encode subject
    const encodedSubject = `=?utf-8?B?${window.btoa(subject)}?=`
    // Construct message
    const messageParts = [
        `From: ${fromName} <${fromEmail}>`,
        `To: ${getNameFromEmail(toEmail)} <${toEmail}>`,
        'Content-Type: text/html; charset=utf-8',
        'MIME-Version: 1.0',
        `Subject: ${encodedSubject}`,
        '',
        messageContents.join('<br>'),
    ]
    const message = messageParts.join('\n')

    console.log(message)

    // Base64 encode message
    const encodedMessage = window
        .btoa(message)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')

    return encodedMessage
}

const sendEmailNew = async (
    toEmail: string,
    fromEmail: string,
    subject: string,
    ...messageContents: string[]
) => {
    const url = 'https://api.litta.tech/v2/gmail/send'

    const message = {
        from: fromEmail,
        fromName: 'LITTA',
        to: toEmail,
        subject,
        messageContents: messageContents,
    } as Record<string, unknown>

    const res = await axios.post(url, message)

    console.log(res.data)
}

export const getWhatsappActiveStatus = async (userId?: string) => {
    if (!userId) return false

    const docRef = doc(db, 'users', userId)
    const snapshot = await getDoc(docRef)

    if (!snapshot.exists()) return false

    return snapshot.data()?.whatsappActive ?? false
}

export const getCurrentUserLabel = async () => {
    const currentUser = getAuth().currentUser
    if (!currentUser) return null

    const docRef = doc(db, 'users', currentUser.uid)
    const snapshot = await getDoc(docRef)

    const customerName = snapshot.exists()
        ? (snapshot.data()?.customer_name as string | undefined)
        : undefined

    return customerName || currentUser.email || null
}

export const sendServiceMessage = async (
    firebaseId: string | undefined,
    isCommercial: boolean,
    messageTitle: string,
    message: string,
    phone: string | undefined,
    reference: string,
    userId: string | undefined,
    user: string,
    email?: string,
) => {
    if (!phone) return [null, null]

    const fromEmail = isCommercial ? 'commercial@litta.co' : 'hi@litta.co'

    // Check if they are eligible to receive whatsapp
    const hasWhatsapp = await getWhatsappActiveStatus(userId)
    const cleansedMessage = message.replace(/(\r\n|\n|\r)/gm, ' ')

    const textBody = {
        channelId: 502974,
        message: {
            type: 'text',
            text: `Important LITTA Service Message: ${cleansedMessage} `,
        },
    }

    const whatsappBody = {
        channelId: 355491,
        message: {
            type: 'whatsapp_template',
            template: {
                name: 'service_message_0325_4',
                languageCode: 'en',
                components: [
                    {
                        type: 'body',
                        text: null,
                        parameters: [
                            {
                                type: 'text',
                                text: reference,
                            },
                            {
                                type: 'text',
                                text: cleansedMessage,
                            },
                        ],
                    },
                ],
            },
        },
    }

    const body = hasWhatsapp ? whatsappBody : textBody

    let emailSent
    try {
        console.log(email)
        if (email) {
            console.log(`Sending email to ${email}`)
            await sendEmailNew(
                email,
                fromEmail,
                'Important LITTA Service Message',
                ...message.split('\n'),
            )
        }
        emailSent = true
    } catch (error) {
        console.log('Error while sending email...')
        console.log(error)
        emailSent = false
    }

    const proxyUrl = `${getLittaPublicApiBaseUrl()}/intercom/message`

    const token = await getAuth().currentUser?.getIdToken()

    let result
    try {
        result = await axios.post(
            proxyUrl,
            {
                phone,
                body,
            },
            {
                headers: { Authorization: `Bearer ${token}` },
            },
        )
    } catch (err) {
        const error = err as AxiosError
        const data = error.response?.data as { message: string }
        return [
            null,
            `${data.message} (${emailSent ? 'Email sent' : 'Email not sent'})`,
        ]
    }
    const { contactId } = result.data

    const respondUrl = `https://app.respond.io/space/55810/${
        contactId ? `message/${contactId}` : ''
    }`

    if (!firebaseId) return [respondUrl, null]

    await updateFirebaseWithServiceMessage(
        firebaseId,
        messageTitle,
        message,
        user,
    )
    await trackInternalEvent(
        'serviceMessageSent',
        firebaseId,
        getAuth().currentUser?.email ?? undefined,
    )
    return [respondUrl, null]
}

export const updateFirebaseWithServiceMessage = async (
    firebaseId: string,
    messageTitle: string,
    message: string,
    user: string,
) => {
    const docRef = doc(db, 'book_orders', firebaseId)
    const eventObject = {
        type: 'serviceMessage',
        title: messageTitle,
        user,
        timestamp: new Timestamp(new Date().getTime() / 1000, 0),
        metadata: {
            message,
        },
        source: 'Beehive',
    }

    try {
        await updateDoc(docRef, {
            keyEventsTimeline: arrayUnion(eventObject),
        })
    } catch (err) {
        console.log('Error updating document', err)
    }
}

export const getMissionControlCards = async () => {
    const ref = collection(db, 'mission_control')
    const q = query(ref, where('active', '==', true))

    const snapshot = await getDocs(q)
    const data = snapshot.docs.map(doc => doc.data() as MissionControl)

    return data
}

export const getLiveOpsCards = async () => {
    const ref = collection(db, 'live_operations')
    const q = query(ref, where('active', '==', true))

    const snapshot = await getDocs(q)
    const data = snapshot.docs.map(doc => doc.data() as LiveOperation)

    return data
}

export const addOrUpdateJobMonitoringDetails = async (
    firebaseId: string,
    recordId: string,
    jobMonitors: JobMonitor[],
    monitorsDiff: { reason: string; isAdded: boolean }[],
    user: string,
) => {
    const docRef = doc(db, 'book_orders', firebaseId)

    console.log('JobMonitor', jobMonitors)

    const message = monitorsDiff
        .reduce(
            (acc, curr) => {
                const index = curr.isAdded ? 0 : 1

                acc[index].count += 1
                acc[index].reasons.push(curr.reason)

                return acc
            },
            [
                { count: 0, reasons: [] },
                { count: 0, reasons: [] },
            ] as { count: number; reasons: string[] }[],
        )
        .reduce((acc, curr, index) => {
            const isAdded = index === 0
            if (curr.count > 0) {
                acc.push(
                    `${isAdded ? 'added' : 'removed'} ${
                        curr.count
                    } job monitor(s): ${curr.reasons.join(', ')}`,
                )
            }
            return acc
        }, [] as string[])
        .join(' and ')

    const eventObject = {
        type: 'jobMonitoring',
        user,
        timestamp: new Timestamp(new Date().getTime() / 1000, 0),
        metadata: {
            message: message.charAt(0).toUpperCase() + message.slice(1),
            jobMonitors,
        },
        source: 'Beehive',
    }

    addKeyEvent(firebaseId, eventObject)

    const finalRagStatus = jobMonitors.some(
        monitor => monitor.ragStatus == 'Red',
    )
        ? 'Red'
        : 'Amber'

    try {
        console.log(`Sending data to Airtable using record ID ${recordId}`)

        if (recordId) {
            await axios.post('https://api.litta.tech/v1/proxy/queue', {
                baseId: 'appyuCUJQH2WF7JDj',
                tableId: 'tblYI3REdC4D6h1iu',
                records: {
                    id: recordId,
                    fields: {
                        rag_status:
                            jobMonitors.length == 0 ? null : finalRagStatus,
                        monitoring_type:
                            jobMonitors.length == 0
                                ? null
                                : jobMonitors.map(
                                      monitor => monitor.monitorType,
                                  ),
                        monitoring_reason:
                            jobMonitors.length == 0
                                ? null
                                : jobMonitors.map(monitor => monitor.subReason),
                    },
                },
            })
        }
    } catch (error) {
        console.log('Error sending monitoring data to Airtable')
        console.log(error)
    }

    try {
        await updateDoc(docRef, {
            jobMonitoring: jobMonitors.length == 0 ? null : jobMonitors,
        })
        return true
    } catch (err) {
        console.log(err)
        return false
    }
}

export const addInternalComment = async (
    firebaseId: string,
    internalCommentObject: InternalComment,
) => {
    const docRef = doc(db, 'book_orders', firebaseId)

    try {
        await updateDoc(docRef, {
            internalComments: arrayUnion(internalCommentObject),
        })
        return true
    } catch (err) {
        console.log(err)
        return false
    }
}

export const getStatusFromDocument = async (firebaseId: string) => {
    const docRef = doc(db, 'book_orders', firebaseId)
    const statusRef = collection(docRef, 'status')

    const snapshot = await getDocs(statusRef)
    const data = snapshot.docs.map(doc => doc.data() as Status)

    return data
}

export const getCalendarRuleOverride = async (
    day: number,
    month: number,
    year: number,
) => {
    const holidaysRef = collection(db, 'calendar_rules_override')
    const q = query(
        holidaysRef,
        where('day', '==', day),
        where('month', '==', month),
        where('year', '==', year),
    )

    const snapshot = await getDocs(q)
    if (snapshot.empty) return null

    return snapshot.docs[0].data() as CalendarRulesOverride
}

export const getCalendarRules = async () => {
    const calendarRulesRef = collection(db, 'calender_rules')
    // const q = query(calendarRulesRef, where("day_type", "==", dayName));

    const snapshot = await getDocs(calendarRulesRef)
    return snapshot.docs.map(doc => doc.data()) as CalendarRule[]
}

export const getPolicyRules = async () => {
    const collectionRef = collection(db, 'policy_fees')
    const snapshot = await getDocs(collectionRef)
    const data = snapshot.docs.map(doc => doc.data()) as PolicyRule[]
    return data
}

export const getCommercialSummaryQuestions = async (suffix: 1 | 2) => {
    const collectionRef = collection(db, 'confirmation')

    const arrayContains = suffix == 1 ? 'book_pay' : 'commercial'

    const q = query(
        collectionRef,
        where('platform', 'array-contains', arrayContains),
    )
    const snapshot = await getDocs(q)

    const data = snapshot.docs.map(doc => doc.data()) as Confirmation[]
    return data
}

export const uploadAnswers = async (
    answers: ConfirmationQuestionAnswer[],
    firebaseId: string,
    recordId?: string,
) => {
    const docRef = doc(db, 'book_orders', firebaseId)

    const individualFields = answers.reduce(
        (acc, curr) => {
            if (curr.fieldName) {
                acc[curr.fieldName] = curr.value
            }
            return acc
        },
        {} as Record<string, unknown>,
    )

    try {
        console.log(`Sending data to Airtable using record ID ${recordId}`)

        if (recordId) {
            await axios.post('https://api.litta.tech/v1/proxy/queue', {
                baseId: 'appyuCUJQH2WF7JDj',
                tableId: 'tblYI3REdC4D6h1iu',
                records: {
                    id: recordId,
                    fields: {
                        ...individualFields,
                    },
                },
            })
        }
    } catch (error) {
        console.log('Error sending monitoring data to Airtable')
        console.log(error)
    }

    try {
        await updateDoc(docRef, {
            confirmationAnswers: answers,
            ...individualFields,
        })
        return true
    } catch (err) {
        console.log(err)
        return false
    }
}

export const addKeyEvent = async (
    documentId: string,
    event: KeyEventsTimeline,
): Promise<boolean> => {
    const docRef = doc(db, 'book_orders', documentId)

    try {
        await updateDoc(docRef, {
            keyEventsTimeline: arrayUnion(event),
        })
        return true
    } catch (err) {
        console.log('Error updating key events', err)
        return false
    }
}

export const getPayoutCategories = async (): Promise<
    PayoutCategory[] | null
> => {
    const payoutCategoriesRef = collection(db, 'payout_categories')
    const queryPayoutCategories = query(payoutCategoriesRef)

    const snapshot = await getDocs(queryPayoutCategories)

    if (snapshot.empty) return null
    return snapshot.docs.map(doc => doc.data() as PayoutCategory)
}

export const addCleanseData = async (
    documentId: string,
    data: CleanseData,
    requiresTippingTicket: boolean,
) => {
    const docRef = doc(db, 'book_orders', documentId)

    const object = {
        carrier_job_description: data.carrierJobDescription,
        team_size: data.teamSize,
        vehicle_type: data.vehicleType,
        image_cleansed: data.imageCleansed,
        payoutType: data.payoutType,
        requiresEmptyVan: data.requiresEmptyVan,
        cleansedBy: data.cleansedBy,
        'appJobDetails.isCompulsory': requiresTippingTicket,
        isImageCleansed: true,
        'userDetails.gender': data.userDetails?.gender,
        potentialBusiness: data?.potentialBusiness,
        isBadlyQuoted: data?.isBadlyQuoted,
        isHazConsignmentNoteRequired: data?.isHazConsignmentNoteRequired,
    } as Record<string, any>

    Object.keys(object).forEach(key =>
        object[key] === undefined ? delete object[key] : {},
    )

    console.log(object)

    try {
        await updateDoc(docRef, object)
        console.log('Successfully updated cleanse data')
    } catch (err) {
        console.log('Error updating cleanse data', err)
    }
}

export const getLoadAndAdditionalItems = async () => {
    const servicePricingRef = collection(db, 'service_pricing')
    const queryLoadAndAdditionalItems = query(
        servicePricingRef,
        and(
            where('active', '==', true),
            or(
                where('type', '==', 'load_increment'),
                and(
                    where('type', '==', 'item'),
                    where('additional_item', '==', true),
                ),
            ),
        ),
    )

    const snapshot = await getDocs(queryLoadAndAdditionalItems)
    const docs = snapshot.docs.map(doc => doc.data() as ServicePricing)

    const separated = docs.reduce(
        (acc, curr) => {
            if (curr.type == 'load_increment') {
                acc.loads.push(curr)
            } else {
                acc.items.push(curr)
            }

            return acc
        },
        { loads: [], items: [] } as {
            loads: ServicePricing[]
            items: ServicePricing[]
        },
    )

    return separated
}

export const getActiveFormItemsFromDb = async (commercialAccount: string) => {
    const servicePricingRef = collection(db, 'service_pricing')
    const queryActiveItems = query(
        servicePricingRef,
        where('active', '==', true),
        where('trackedCustomers', 'array-contains', commercialAccount),
    )

    const snapshot = await getDocs(queryActiveItems)
    const docs = snapshot.docs.map(doc => doc.data() as ServicePricing)

    return docs
}

export const getActiveServicePricingItemsFromDb = async () => {
    const servicePricingRef = collection(db, 'service_pricing')
    const queryActiveItems = query(
        servicePricingRef,
        where('active', '==', true),
    )

    const snapshot = await getDocs(queryActiveItems)
    const docs = snapshot.docs.map(doc => doc.data() as ServicePricing)

    return docs
}

export const addRejectedImageNotes = async (
    documentId: string,
    selectedLoad: string,
    additionalItems: string | null,
) => {
    const docRef = doc(db, 'book_orders', documentId)

    const data = {
        image_cleansed: 'Rejected - More waste',
        imageCleanseNotes: {
            load: selectedLoad,
        } as Record<string, unknown>,
    }

    if (additionalItems)
        data.imageCleanseNotes.additionalItems = additionalItems

    await updateDoc(docRef, data)
}

const fieldMapping = {
    email: 'Email',
    phoneNumber: 'Mobile number',
    additionalMobile: 'Mobile number',
    address: 'Address of Waste',
    description: 'job_description',
    'carrier job description': 'carrier_job_description',
}

export const updateDetails = async (
    value: string,
    oldValue: string | null,
    key: string,
    rawKey: string,
    recordId: string,
    documentId: string,
    user: string,
    alsoSet: Record<string, unknown> = {},
) => {
    console.log(`Updating details with key ${key}`)

    await addKeyEvent(documentId, {
        type: 'detailChange',
        user,
        timestamp: new Timestamp(new Date().getTime() / 1000, 0),
        source: 'Beehive',
        metadata: {
            message: formatDetailChangeMessage(rawKey, oldValue, value),
        },
    })

    const docRef = doc(db, 'book_orders', documentId)

    try {
        console.log('Updating Firebase')
        const data = {
            [key]: value,
            ...alsoSet,
        }
        console.log(data)
        await updateDoc(docRef, data)
    } catch (err) {
        console.log('ERROR UPDATING DETAILS ON FIREBASE')
    }

    console.log('Updating Airtable')
    const fieldName =
        fieldMapping[rawKey as keyof typeof fieldMapping] ?? rawKey
    await axios.post('https://api.litta.tech/v1/proxy/queue', {
        baseId: 'appyuCUJQH2WF7JDj',
        tableId: 'tblYI3REdC4D6h1iu',
        records: {
            id: recordId,
            fields: {
                [fieldName]: value,
            },
        },
    })
}

export const carrierDescriptionEditFields = (name: string) => ({
    carrierJobDescriptionEditedAt: serverTimestamp(),
    carrierJobDescriptionEditedBy: {
        name,
        uid: getAuth().currentUser?.uid ?? null,
    },
})

export const handCarrierDescriptionBackToLara = (documentId: string) =>
    updateDoc(doc(db, 'book_orders', documentId), {
        carrierJobDescriptionEditedAt: deleteField(),
        carrierJobDescriptionEditedBy: deleteField(),
    })

export const updateBooleanValue = async (
    value: boolean,
    firebaseFieldName: string,
    airtableFieldName: string | undefined,
    documentId: string,
    recordId: string,
    user?: string,
    airtableFieldValueOverride?: string | null,
) => {
    const docRef = doc(db, 'book_orders', documentId)

    if (airtableFieldName) {
        await axios.post('https://api.litta.tech/v1/proxy/queue', {
            baseId: 'appyuCUJQH2WF7JDj',
            tableId: 'tblYI3REdC4D6h1iu',
            records: {
                id: recordId,
                fields: {
                    [airtableFieldName]:
                        airtableFieldValueOverride != undefined
                            ? airtableFieldValueOverride
                            : value,
                },
            },
        })
    }

    const event: KeyEventsTimeline = {
        type: 'UpdateBooleanValue',
        user: user ?? 'Unknown',
        timestamp: new Timestamp(new Date().getTime() / 1000, 0),
        source: 'Beehive',
        metadata: {
            message: `${user} set ${airtableFieldName} to ${value}`,
        },
    }

    try {
        await updateDoc(docRef, {
            [firebaseFieldName]: value,
            keyEventsTimeline: arrayUnion(event),
        })
    } catch (err) {
        console.log(
            `Error updating Firebase document ${documentId} with value ${value}`,
        )
    }
}

export const updateAirtable = async (
    value: any,
    airtableFieldName: string,
    recordId: string,
    documentId?: string,
    firebaseFieldName?: string,
    firebaseValue?: any,
    customObject?: boolean,
) => {
    console.log(firebaseFieldName, '->', firebaseValue)

    await axios.post('https://api.litta.tech/v1/proxy/queue', {
        baseId: 'appyuCUJQH2WF7JDj',
        tableId: 'tblYI3REdC4D6h1iu',
        records: {
            id: recordId,
            fields: {
                [airtableFieldName]: value,
            },
        },
    })

    if (!documentId) return

    const docRef = doc(db, 'book_orders', documentId)

    const fieldName = firebaseFieldName ?? airtableFieldName
    const fieldValue = firebaseValue ?? value

    try {
        const updateObject = customObject
            ? firebaseValue
            : {
                  [fieldName]: fieldValue,
              }

        console.log('updateObject ->', updateObject)

        await updateDoc(docRef, updateObject)
    } catch (err) {
        console.log(
            `Error updating Firebase document ${documentId} with value ${fieldValue} at field ${fieldName}`,
        )
    }
}

export const updateDocByRef = async (documentId: string, object: any) => {
    const docRef = doc(db, 'book_orders', documentId)

    try {
        await updateDoc(docRef, object)
    } catch (error) {
        console.log(error)
    }
}

export const getPolicyFees = async (name?: string) => {
    const collRef = collection(db, 'policy_fees')

    const q = name ? query(collRef, where('Name', '==', name)) : collRef

    try {
        const result = await getDocs(q)
        return result.docs.map(doc => doc.data())
    } catch (error) {
        console.log(error)
    }
}

export const getReasons = async (type: 'rearrange' | 'cancel') => {
    const collRef = collection(db, 'fail_reasons')
    const q = query(collRef, where(type, '==', true))

    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => doc.data())
}

export const updateRemainingWasteDetails = async (
    documentId: string,
    remainingWasteDetails: Record<string, unknown>,
) => {
    const docRef = doc(db, 'book_orders', documentId)

    try {
        await updateDoc(docRef, { remainingWasteDetails })

        await addKeyEvent(documentId, {
            type: 'updatedRemainingWasteDetails',
            user: remainingWasteDetails.resolvedBy as string,
            timestamp: new Timestamp(new Date().getTime() / 1000, 0),
            source: 'Beehive',
            metadata: {
                message: `Remaining waste action resolved by ${remainingWasteDetails.resolvedBy} via Beehive`,
            },
        })
    } catch (error) {
        console.log(error)
    }
}

export const updateAwaitingPayment = async (
    documentId: string,
    addPayLogId: string,
    addPayLogRef: string,
    items: Item[] | string,
    manualAdjustment: number,
    amount: number,
    description: string,
    requestedBy: string,
    type: string,
    timeslot?: string,
    subReason?: string,
    imageCleansed?: string,
    additionalFees?: AdditionalFee[],
    sentToCustomer = true,
    dismantle?: Dismantle[],
) => {
    const docRef = doc(db, 'book_orders', documentId)
    const awaitingPaymentDetails = {
        addPayLogId,
        addPayLogRef,
        items,
        manualAdjustment,
        amount,
        description,
        requestedBy,
        type,
        resolved: false,
        sentToCustomer,
        ...(imageCleansed && { imageCleansed }),
        ...(subReason && { subReason }),
        ...(additionalFees && additionalFees.length > 0 && { additionalFees }),
        ...(dismantle && { dismantle }),
    } as Record<string, unknown>

    if (type == 'Update timeslot/date with fee') {
        delete awaitingPaymentDetails.items
        awaitingPaymentDetails.date = items
        awaitingPaymentDetails.timeslot = timeslot
    }

    try {
        await updateDoc(docRef, {
            awaitingPaymentDetails,
        })
    } catch (error) {
        console.log(error)
    }
}

export const markAwaitingPaymentSent = (documentId: string) =>
    updateDoc(doc(db, 'book_orders', documentId), {
        'awaitingPaymentDetails.sentToCustomer': true,
    })

export const getCommercialAccount = async (externalAccountRef?: string) => {
    if (!externalAccountRef) return null

    const collRef = collection(db, 'commercialAccounts')
    const queryRef = query(
        collRef,
        where('externalAccountRef', '==', externalAccountRef),
    )

    const snapshot = await getDocs(queryRef)

    if (snapshot.empty) return null
    return snapshot.docs[0].data() as CommercialAccount
}

export const getEwcCodes = async () => {
    const collRef = collection(db, 'ewc_codes')
    const snapshot = await getDocs(collRef)

    if (snapshot.empty) return []
    return snapshot.docs.map(doc => doc.data().code)
}

export const getPayoutReasons = async () => {
    const collRef = collection(db, 'payout_review')
    const queryRef = query(
        collRef,
        where('type', '==', 'payout_adjustment_reason'),
        where('display_to', 'array-contains', 'special_payment_arrangement'),
    )
    const snapshot = await getDocs(queryRef)

    if (snapshot.empty) return []
    return snapshot.docs.map(doc => doc.data() as PayoutReview)
}

export const addPayoutReview = async (
    documentId: string,
    payoutObject: Record<string, unknown>,
) => {
    const docRef = doc(db, 'book_orders', documentId)

    await updateDoc(docRef, {
        payoutHistory: arrayUnion(payoutObject),
        payoutType: 'SPA',
    })
}

export const updateFirebase = async (
    documentId: string,
    object: Record<string, any>,
    fieldName?: string,
    fieldValue?: unknown,
    userName?: string,
) => {
    const docRef = doc(db, 'book_orders', documentId)

    let finalObject = object

    console.log({ fieldName, fieldValue, userName })

    if (fieldName && fieldValue !== undefined && userName) {
        const eventObject = {
            type: 'tablesCheckbox',
            title: 'Checked box on tables',
            user: userName,
            timestamp: new Timestamp(new Date().getTime() / 1000, 0),
            metadata: {
                message: `Updated ${fieldName} to ${fieldValue}`,
            },
            source: 'Beehive',
        }

        finalObject = {
            ...finalObject,
            keyEventsTimeline: arrayUnion(eventObject),
        }
    }

    await updateDoc(docRef, finalObject)
}

export const getTable = async (tableName: string) => {
    const collRef = collection(db, 'tables')
    const whereQuery = where('route', '==', tableName)
    const queryRef = query(collRef, whereQuery)
    const snapshot = await getDocs(queryRef)

    return snapshot.empty ? null : snapshot.docs[0].data()
}

export const getRefundAmount = async (documentId: string) => {
    const collRef = collection(
        doc(db, 'book_orders', documentId),
        'payment_refunds',
    )
    const snapshot = await getDocs(collRef)

    if (snapshot.empty) return 0

    const totalAmount = snapshot.docs[0].data().data.object.amount_refunded
    // const totalAmount = snapshot.docs.reduce((acc, curr) => {
    //     const data = curr.data();
    //     return (acc += data.data.object.amount_refunded);
    // }, 0);
    const finalAmount = parseFloat((totalAmount / 100).toFixed(2))

    return finalAmount
}

/**
 * Refund requested (e.g. on cancellation) but not yet issued through Stripe, in pounds.
 * Cancellations always write a request, so zero-amount requests are ignored.
 */
export const getRequestedRefundAmount = async (documentId: string) => {
    const snapshot = await getDocs(
        collection(doc(db, 'book_orders', documentId), 'refunds_requests'),
    )

    const total = snapshot.docs.reduce((acc, refundRequest) => {
        const amount = Number(refundRequest.data().refund_amount)
        return Number.isFinite(amount) && amount > 0 ? acc + amount : acc
    }, 0)

    return parseFloat(total.toFixed(2))
}

export const createRefund = async (
    documentId: string,
    refundAmount: number,
    requestedBy: string,
    reason: string,
    detail: string,
) => {
    const endpoint = isStaging
        ? 'https://europe-west2-staging-9c37f.cloudfunctions.net/refund_payment'
        : 'https://europe-west2-book-litta-co.cloudfunctions.net/refund_payment'

    const payload = {
        refund_amount: refundAmount,
        firebase_id: documentId,
        status: 'submitted',
        requested_by: requestedBy,
        refund_reason: reason,
        refund_reason_detail: detail,
    }

    try {
        const { data } = await axios.post(endpoint, { data: payload })
        if (data.result.error) {
            return false
        }
        return true
    } catch (err) {
        console.log('refund error', err)
        return false
    }
}

export const getAdditionalPaymentsAmount = async (documentId: string) => {
    const collRef = collection(
        doc(db, 'book_orders', documentId),
        'additional_payments',
    )
    const snapshot = await getDocs(collRef)

    if (snapshot.empty) return 0

    const totalAmount = snapshot.docs.reduce((acc, curr) => {
        const data = curr.data()
        return (acc +=
            data.data.object.amount || data.data.object.amount_subtotal)
    }, 0)
    const finalAmount = parseFloat((totalAmount / 100).toFixed(2))

    return finalAmount
}

export const getFirebaseDocument = async (coll: string, documentId: string) => {
    const docRef = doc(db, coll, documentId)
    const snapshot = await getDoc(docRef)

    return snapshot.exists() ? snapshot.data() : null
}

export const updateFirebaseDocument = async (
    coll: string,
    documentId: string,
    payload: Record<string, any>,
) => {
    const docRef = doc(db, coll, documentId)

    try {
        await updateDoc(docRef, payload)
        return true
    } catch (err) {
        console.log(err)
        return false
    }
}

export const createFirebaseDocument = async (
    coll: string,
    payload: Record<string, any>,
): Promise<string | null> => {
    try {
        const collRef = collection(db, coll)
        const docRef = await addDoc(collRef, payload)
        return docRef.id
    } catch (err) {
        console.log(err)
        return null
    }
}

export const checkAndUpdateIfComplete = async (order: BookOrder) => {
    const documentId = order.recordId
    if (!documentId) {
        return false
    }

    const isCommercial = order?.client_type === 'commercial'
    const isReviewed = order?.isInternalReviewed
    const isJobCardComplete =
        order?.jobCardEmailed ||
        order?.job_card === 'Do Not Send.' ||
        order?.job_card_status === 'do not send'
    const isWasteTransferNoteComplete = order?.isWasteTransferEmailed
    const isTrustpilotComplete = order?.trustpilot_sent || order?.noFeedbackFlag

    const isCommercialReviewFullyComplete =
        isCommercial &&
        isReviewed &&
        isJobCardComplete &&
        isWasteTransferNoteComplete

    const isPaidReviewFullyComplete =
        !isCommercial && isReviewed && isJobCardComplete && isTrustpilotComplete

    if (isCommercialReviewFullyComplete || isPaidReviewFullyComplete) {
        const data = {
            jobReviewStatus: {
                isComplete: true,
                completedAt: new Date(),
            },
        }

        try {
            const docRef = doc(db, 'book_orders', documentId)
            await updateDoc(docRef, data)
            return true
        } catch (err) {
            return false
        }
    }
}

export const getPostcodes = async (): Promise<PostcodesData[]> => {
    const postcodesQuery = query(
        collection(db, 'postCodes_surge'),
        orderBy('postcode_area'),
    )

    const result = await getDocs(postcodesQuery)

    return result.docs.map(doc => doc.data() as PostcodesData)
}

export const getRefForDay = async (
    day: number,
    month: number,
    year: number,
) => {
    const whereByDay = where('day', '==', day)
    const whereByMonth = where('month', '==', month)
    const whereByYear = where('year', '==', year)

    const queryDataForDay = query(
        holidaysRef,
        whereByDay,
        whereByMonth,
        whereByYear,
    )

    const result = await getDocs(queryDataForDay)

    if (result.size == 0) return null
    return result.docs[0].ref
}

export const upsertHolidays = async (holiday: Holiday): Promise<void> => {
    const dayRef = await getRefForDay(holiday.day, holiday.month, holiday.year)

    if (dayRef) {
        await updateDoc(dayRef, { ...holiday })
        return
    }

    await addDoc(holidaysRef, holiday)
}

export const getRefForSurgeGroup = (documentId: string) =>
    doc(db, 'surge_groups', documentId)

export const upsertSurgeGroups = async (
    documentId: string,
    surgeGroups: Surge[],
    date: Date,
): Promise<void> => {
    const ref = getRefForSurgeGroup(documentId)

    const d = new Date(date)
    d.setHours(12, 0, 0, 0)

    await setDoc(ref, { groups: surgeGroups, date: d })
}

export const getRefForCalendarRule = async (
    day: number,
    month: number,
    year: number,
) => {
    const whereByDay = where('day', '==', day)
    const whereByMonth = where('month', '==', month)
    const whereByYear = where('year', '==', year)

    const queryDataForDay = query(
        calendarRulesOverrideRef,
        whereByDay,
        whereByMonth,
        whereByYear,
    )

    const result = await getDocs(queryDataForDay)

    if (result.size == 0) return null
    return result.docs[0].ref
}

export const upsertCalendarRule = async (
    calendarRule: CalendarRulesOverride,
): Promise<void> => {
    const dayRef = await getRefForCalendarRule(
        calendarRule.day,
        calendarRule.month,
        calendarRule.year,
    )

    if (dayRef) {
        await updateDoc(dayRef, { ...calendarRule })
        return
    }

    await addDoc(calendarRulesOverrideRef, calendarRule)
}

export const getClosedDays = async (from: Date, to: Date) => {
    const ref = collection(db, 'holidays')
    const q = query(ref, where('date', '>=', from), where('date', '<=', to))

    const snapshot = await getDocs(q)
    const data = snapshot.docs.map(doc => doc.data() as Holiday)

    return data
}

export const getDocumentId = async <T>(
    reference: string,
    type: 'Order' | 'Quote',
): Promise<[string, T] | []> => {
    const ref = collection(db, 'book_orders')
    console.log(`Getting ID and data for ${reference} of type ${type}`)
    const q = query(
        ref,
        where(type === 'Order' ? 'LTA' : 'quote_ref', '==', reference),
    )

    try {
        const snapshot = await getDocs(q)
        if (snapshot.empty) return []

        return [snapshot.docs[0].id, snapshot.docs[0].data() as T]
    } catch (err) {
        console.log(err)
        return []
    }
}

export const getDocumentByFirebaseId = async <T>(
    firebaseId: string,
): Promise<[string, T] | []> => {
    const ref = doc(db, 'book_orders', firebaseId)

    try {
        const snapshot = await getDoc(ref)

        return [firebaseId, snapshot.data() as T]
    } catch (err) {
        console.log(err)
        return []
    }
}

export const createEmptySite = async () => {
    const ref = collection(db, 'commercialSites')
    const { id } = await addDoc(ref, {})
    return id
}

export const updateSite = async (siteId: string, site: CommercialSite) => {
    const ref = doc(db, 'commercialSites', siteId)
    try {
        await setDoc(ref, site)
        return true
    } catch (err) {
        console.log('err', err)
        return false
    }
}

export const removeFromCallList = async (
    userEmail: string,
    documentId: string,
    callList: List[],
) => {
    const ref = doc(callListRef, userEmail)
    await updateDoc(ref, {
        list: callList.filter(list => list.id !== documentId),
    })
    const date = new Date()
    await updateFirebase(documentId, {
        callListClickedAt: date,
        callListClickedHistory: arrayUnion({
            email: userEmail,
            timestamp: date,
        }),
    })
}

export const addDriverDetails = async (
    documentId: string,
    object: Record<string, any>,
) => {
    const document = doc(db, 'book_orders', documentId)
    try {
        await updateDoc(document, object)
        return true
    } catch (err) {
        console.log(err)
        return false
    }
}

export const submitFeedbackToFirestore = async (
    data: Record<string, unknown>,
    collectionName = 'betaPriceFeedback',
) => {
    const ref = collection(db, collectionName)
    try {
        const result = await addDoc(ref, data)
        console.log(result)
        return true
    } catch (err) {
        console.log(err)
        return false
    }
}

export const getPayoutFeedbackById = async (id: string) => {
    const ref = collection(db, 'betaPayoutFeedback')
    try {
        const collQuery = query(ref, where('documentId', '==', id))
        const result = await getDocs(collQuery)
        return !result.empty
            ? (result.docs[0].data() as Record<string, unknown>)
            : null
    } catch (err) {
        return null
    }
}

export const getDisciplinaryActionById = async (id: string) => {
    const collRef = collection(db, 'carrierDisciplinaryActions')
    const discQuery = query(collRef, where('firebaseId', '==', id))
    const snapshot = await getDocs(discQuery)
    if (snapshot.empty) return null
    const doc = snapshot.docs[0]
    const data = doc.data() as DisciplinaryAction
    return data
}
